import styled, { keyframes } from 'styled-components';
import { colors, spacing, mixins, borderRadius, transitions } from '../../design-system';
import { tokens } from 'design-tokens/warwick/tokens';

/**
 * TV Channels List - Warwick Design System
 * Based on Figma specs: 1920x1080 screen
 */

// TV channels page row — NO background here (TEP native video paints below HTML in the right column).
export const TVChannelsOuterRow = styled.div.attrs(() => ({
  'data-tizen-outer-container': 'true',
}))`
  display: flex;
  position: relative;
  flex: 1;
  min-height: 0;
  background: transparent;
`;

// Glass background only behind category + channel list, not over the video column.
export const TVChannelsLeftGroup = styled.div`
  display: flex;
  flex-shrink: 0;
  background: ${tokens.primitive.color.whiteAlpha60};
  position: relative;
  z-index: 2;
`;

// Channel List Area Container
// Width: 354px (fixed per Figma spec)
// Background: #ffffff 84%
// Border radius: 24.7px
export const ChannelListArea = styled.div`
  width: 354px;
  background: ${tokens.semantic.color.background.screen};
  ${mixins.flexColumn}
  padding: ${tokens.primitive.space[24]};
  overflow: hidden;
`;

// Header
// Top margin: 21px from subtitle
export const ChannelListHeader = styled.div`
  margin-bottom: ${tokens.primitive.space[30]};
  flex-shrink: 0;
`;

// Title: 24px, weight 600
export const ChannelListTitle = styled.h1`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[26]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onLight};
  margin: 0 0 ${tokens.primitive.space[4]} 0;
`;

// Subtitle: 15px, weight 300
export const ChannelCount = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[17]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onLight};
`;

// Scrollable Channel List
// Gap between items: 15px
export const ChannelListContainer = styled.div`
  ${mixins.flexColumn}
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  > * + * {
    margin-top: ${tokens.primitive.space[12]} !important;
  }

  /* Custom scrollbar styling for Tizen compatibility */
  &::-webkit-scrollbar {
    width: ${tokens.primitive.space[8]};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.semantic.color.background.sidebar};
    border-radius: ${tokens.primitive.radius[28]};
  }
`;

// Individual Channel Item
// Height: 46px
// Radius: 13px
// Logo padding: 7px top/bottom, 18px left/right
export const ChannelItem = styled.div`
  display: flex;
  align-items: center;

  padding: ${tokens.primitive.space[12]};
  border-radius: ${tokens.primitive.radius[13]};
  cursor: pointer;
  background: ${(props) =>
    props.active
      ? tokens.semantic.color.background.rowActive
      : tokens.semantic.color.background.rowDefault};
  box-shadow: ${(props) =>
    props.active && props.sectionFocused ? tokens.primitive.shadow.focusRing : 'none'};
  margin: 3px;
`;

// Channel Logo - square, white background
export const ChannelLogo = styled.div`
  width: ${tokens.primitive.size.icon.xlg};
  height: ${tokens.primitive.size.icon.xlg};
  border-radius: ${tokens.primitive.radius[8]};
  overflow: hidden;
  background: ${tokens.semantic.color.background.logoContainer};
  ${mixins.flexCenter}
  flex-shrink: 0;
  margin-inline-end: ${tokens.primitive.space[20]};

  img {
    padding: ${tokens.primitive.space[5]};
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const ChannelInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

// Channel text: 20px, weight 400, uppercase
export const ChannelName = styled.h3`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.normal};
  color: ${tokens.semantic.color.text.onDark};
  margin: 0;
  text-transform: uppercase;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

// Right Video Player Area
// Border radius: 28px (per Figma spec)
// Background: dark gradient (idle state). On TEP devices TizenTEPPlayer sets
// this to 'transparent' via DOM after play() so the native avplay compositor
// layer (rendered BELOW the HTML layer) shows through.
// data-tizen-video-area: DOM anchor for TizenTEPPlayer preview setDisplayRect
export const VideoPlayerArea = styled.div.attrs(() => ({
  'data-tizen-video-area': 'true',
}))`
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 50%, #1a1a1a 100%);
  border-radius: ${tokens.primitive.radius[28]};
  overflow: hidden;
`;

/** Radio now-playing pane — fills VideoPlayerArea (same bounds as TV video). */
export const RadioPreviewPane = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
`;

// Radio watch mode — full content area below header, solid black (no wallpaper bleed-through).
export const RadioNowPlayingStage = styled.div`
  position: fixed;
  top: 127px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background-color: ${tokens.primitive.color.black};
  box-sizing: border-box;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

// Back button slot — fixed top-right, same region as TV PageHeader.
export const RadioBackHeader = styled.div`
  position: fixed;
  top: 127px;
  right: ${tokens.primitive.space[40]};
  z-index: 3;
  pointer-events: auto;
`;

// =============================================================================
// CATEGORY PANEL
// =============================================================================

// Leftmost panel — purple sidebar housing the category list
export const CategoryPanelArea = styled.div`
  width: 15%;
  min-width: 290px;
  background: ${tokens.semantic.color.background.sidebar};
  ${mixins.flexColumn}
  padding: ${tokens.primitive.space[24]};
  flex-shrink: 0;
`;

// "Categories" heading
export const CategoryPanelHeader = styled.h2`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[26]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onDark};
  margin: 0 0 ${tokens.primitive.space[30]} 0;
`;

// Vertical stack of category pills
export const CategoryListContainer = styled.div`
  ${mixins.flexColumn}
  > * + * {
    margin-top: ${tokens.primitive.space[12]} !important;
  }
  overflow-y: auto;

  /* Custom scrollbar styling for Tizen compatibility */
  &::-webkit-scrollbar {
    width: ${tokens.primitive.space[8]};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.primitive.color.gold};
    border-radius: ${tokens.primitive.radius[28]};
  }
`;

// =============================================================================
// CHANNEL LOADING OVERLAY
// =============================================================================

const dotCycle = keyframes`
  0%, 100% { opacity: 0.5; }
  33%       { opacity: 0.6; }
  66%       { opacity: 0.7; }
`;

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.28; transform: scale(1); }
  50%      { opacity: 0.48; transform: scale(1.08); }
`;

const centerDotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50%      { transform: scale(1.18); opacity: 1; }
`;

// Covers the video player area (preview) or viewport (fullscreen) while a channel loads.
// Explicit legacy flex prefixes — Tizen WebKit often ignores transform centering and plain flex.
export const ChannelLoadingBackdrop = styled.div`
  position: ${(props) => (props.$fullscreen ? 'fixed' : 'absolute')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: ${(props) => (props.$fullscreen ? 150 : 100)};
  box-sizing: border-box;
  /* Fullscreen: solid black (radio/TV watch). Preview: dimmed panel over video column. */
  background: ${(props) =>
    props.$fullscreen ? tokens.primitive.color.black : 'rgba(0, 0, 0, 0.72)'};
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  pointer-events: none;
`;

// Centered card — soft entrance so channel zaps feel intentional
export const ChannelLoadingCard = styled.div`
  ${mixins.flexColumn}
  align-items: center;
  background: ${tokens.semantic.color.background.welcomeDialog};
  border-radius: ${tokens.primitive.radius[24]};
  box-shadow: ${tokens.primitive.shadow.overlay};
  animation: ${cardEnter} 0.35s ease-out;
`;

// Spinner wrapper — gold glow blob behind the arc ring (breathing pulse = "about to start")
export const SpinnerGlow = styled.div`
  position: relative;
  padding: ${tokens.primitive.space[66]} ${tokens.primitive.space[136]};
  ${mixins.flexCenter}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${tokens.primitive.radius.full};
    background: ${tokens.primitive.color.gold};
    filter: blur(64px);
    opacity: 0.35;
    pointer-events: none;
    animation: ${glowPulse} 1.6s ease-in-out infinite;
  }
`;

const spinArc = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// Rotating arc ring around the center dot
export const SpinnerArc = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${tokens.primitive.radius.full};
  border: 3px solid ${tokens.primitive.color.whiteAlpha30};
  border-top-color: ${tokens.primitive.color.gold};
  animation: ${spinArc} 1s linear infinite;
  ${mixins.flexCenter}
  position: relative;
`;

// Solid gold center dot inside the arc — gentle pulse while waiting for stream
export const SpinnerDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: ${tokens.primitive.radius.full};
  background: ${tokens.primitive.color.gold};
  position: absolute;
  animation: ${centerDotPulse} 1.6s ease-in-out infinite;
`;

// Channel name below the spinner
export const ChannelLoadingName = styled.p`
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[24]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onDark};
  text-transform: uppercase;
  margin: 0 0 ${tokens.primitive.space[24]} 0;
  letter-spacing: ${tokens.primitive.typography.letterSpacing.button};
`;

// Replaces spinner after timeout / play failure — same card, calm status copy
export const ChannelUnavailableStatus = styled.div`
  position: relative;
  padding: ${tokens.primitive.space[66]} ${tokens.primitive.space[136]};
  ${mixins.flexCenter}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${tokens.primitive.radius.full};
    background: ${tokens.primitive.color.gold};
    filter: blur(64px);
    opacity: 0.22;
    pointer-events: none;
  }
`;

export const ChannelUnavailableMessage = styled.p`
  position: relative;
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[26]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.dialogAccent};
  text-align: center;
  margin: 0;
  letter-spacing: ${tokens.primitive.typography.letterSpacing.button};
`;

// Three cycling dots
export const LoadingDotsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${tokens.primitive.space[32]};

  /* Child spacing — explicit LTR/RTL fallback for Tizen compatibility */
  & > * + * {
    margin-left: ${tokens.primitive.space[8]};
  }

  [dir='rtl'] & > * + * {
    margin-left: 0;
    margin-right: ${tokens.primitive.space[8]};
  }
`;

export const LoadingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${tokens.primitive.radius.full};
  background: ${tokens.primitive.color.gold};
  animation: ${dotCycle} 1.2s ease-in-out infinite;
  animation-delay: ${(props) => props.delay ?? '0s'};
`;

// Fullscreen OSD — classic TV channel number, visible only with loading overlay
export const FullscreenChannelNumber = styled.div`
  position: absolute;
  top: ${tokens.primitive.space[40]};
  left: ${tokens.primitive.space[48]};
  z-index: 101;
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[40]};
  font-weight: ${tokens.primitive.typography.fontWeight.semiBold};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onDark};
  letter-spacing: ${tokens.primitive.typography.letterSpacing.button};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.65);
  animation: ${cardEnter} 0.35s ease-out;
  pointer-events: none;
`;

// Individual category pill
// margin below is so that box shadow is not clipped due to overflow of container
export const CategoryItem = styled.div`
  ${mixins.flexCenterVertical}
  padding: ${tokens.primitive.space[20]};
  border-radius: ${tokens.primitive.radius[14]};
  cursor: pointer;
  background: ${(props) =>
    props.active
      ? tokens.semantic.color.background.rowActive
      : tokens.semantic.color.background.categoryDefault};
  font-family: ${tokens.primitive.typography.fontFamily.base};
  font-size: ${tokens.primitive.typography.fontSize[20]};
  font-weight: ${tokens.primitive.typography.fontWeight.regular};
  line-height: ${tokens.primitive.typography.lineHeight.tight};
  color: ${tokens.semantic.color.text.onDark};
  box-shadow: ${(props) =>
    props.active && props.sectionFocused ? tokens.primitive.shadow.focusRing : 'none'};
  margin: 3px;
`;
